import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IColumnConfig, IColumnFilter } from 'src/app/shared/interfaces/ITable';
import { FilterType } from 'src/app/shared/enums';
import { DATE_FORMATS, DATE_PICKER_LOCALE, FILTER_COLUMN_TYPE, TABLE_COLUMN_FILTER_LIST } from 'src/app/shared/constants';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule, DatePipe, NgComponentOutlet } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MaterialModule } from 'src/app/material.module';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { FormDatePickerComponent } from '../../form-date-picker/form-date-picker.component';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DatePickerHeader } from './date-picker-header/date-picker-header.component';

@Component({
  standalone: true,
  selector: 'app-filter-header',
  templateUrl: './filter-header.component.html',
  styleUrls: ['./filter-header.component.scss'],
  imports: [MatMenuModule, CommonModule, MatDatepickerModule, MaterialModule, TranslateModule, FormsModule, MatInputModule],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: DATE_PICKER_LOCALE },
    DatePipe
  ]
})
export class FilterHeaderComponent implements OnChanges {
  @ViewChild(FormDatePickerComponent) datePickerComponent!: FormDatePickerComponent;
  @ViewChild('dateFilterInput') dateFilterInput: ElementRef<HTMLInputElement>;
  @ViewChild('textFilterInput') textFilterInput: ElementRef<HTMLInputElement>;
  @ViewChild('customTopRowOutlet') customTopRowOutlet: NgComponentOutlet;
  @Input() column: IColumnConfig;
  @Input() currentColumnFilterText: { [key: string]: any } = {};
  @Input() currentColumnFilterOperator: { [key: string]: any } = {};
  @Output() columnFilterEvent = new EventEmitter<any>();
  filterText: string;
  columnFilter: IColumnFilter;
  datePickerHeader = DatePickerHeader;
  FilterType = FilterType;

  get isDate(): boolean {
    return this.column.filterable === FilterType.DATE;
  }

  get Input(): HTMLInputElement {
    return this.isDate ? this.dateFilterInput?.nativeElement : this.textFilterInput?.nativeElement;
  }

  ngOnChanges(changes: SimpleChanges) {
    const columnChange = changes['column'];
    const filterOperatorChange = changes['currentColumnFilterOperator'];
    const filterTextChange = changes['currentColumnFilterText'];
  
    if (columnChange) {
      const columnKey = columnChange.currentValue?.key;
      if (filterOperatorChange?.currentValue && Object.keys(filterOperatorChange.currentValue).length !== 0) {
        this.columnFilter = filterOperatorChange.currentValue[columnKey];
      } else {
        this.columnFilter = this.getResetOperator(this.column?.filterable);
      }
      this.filterText = filterTextChange?.currentValue?.[columnKey] ?? '';
    }
  }

  getResetOperator(filterType: FilterType = FilterType.TEXT) {
    return {
      ...TABLE_COLUMN_FILTER_LIST[filterType === FilterType.TEXT ? 0 : 1],
      icon: TABLE_COLUMN_FILTER_LIST[0].icon
    };
  }

  getOperatorByType(filterType: FilterType) {
    return TABLE_COLUMN_FILTER_LIST.filter((operator) => operator.filterType.includes(filterType));
  }

  onChangeColumnFilter(columnFilter: IColumnFilter) {
    this.columnFilter = columnFilter;
    setTimeout(() => {
      this.Input?.focus();
    }, 0);
  }

  isFocusing(columnFilter: IColumnFilter) {
    return this.columnFilter.paramOperator === columnFilter.paramOperator;
  }

  clearFilter() {
    if (this.column.filterable === FilterType.CUSTOM) {
      this.customTopRowOutlet?.componentInstance?.clearInput?.();
      return;
    }
    if (this.Input) {
      this.columnFilter = this.getResetOperator(this.column.filterable);
      this.filterText = '';
      this.Input.value = '';
      this.columnFilterEvent.emit({ type: FILTER_COLUMN_TYPE.FILTER });
    }
  }

  reset() {
    this.columnFilterEvent.emit({ type: FILTER_COLUMN_TYPE.RESET });
  }

  onFilterByColumn(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.columnFilterEvent.emit({ type: FILTER_COLUMN_TYPE.FILTER });
    }
  }

  // Only run when change date in datepicker
  onFilterDateChange = () => {
    setTimeout(() => {
      this.dateFilterInput.nativeElement.focus();
    }, 0);
  };

  // Only run when change text in input
  onFilterNonDateChange(event: any) {
    let inputValue: string = event.target.value;
    // Remove non-numeric characters using a regular expression
    if (this.column.filterable === FilterType.NUMBER) {
      inputValue = inputValue.replace(/[^0-9]/g, '');
      (event.target as HTMLInputElement).value = inputValue;
    }
    // Do not use 2-way binding or cause some unwanted behavior
    this.filterText = inputValue;
  }
}
